import React from "react";
import { useState, useEffect, useRef } from "react";

export function StickyContainer({
  children,
  className = "",
  stickyClass = "",
  notStickyClass = "",
  top = "top-0",
  forceWidth = true,
  triggerFromBottom = false,
  stickyStateChanged = () => {},
}) {
  const [isSticky, setIsSticky] = useState(false);
  const [initialWidth, setInitialWidth] = useState(0);
  const [tempHeight, setTempHeight] = useState(0);
  const ref = useRef(null);
  const tempRef = useRef(null);
  const initialLoad = useRef(true);

  const handleScroll = (top) => {
    const scrolly = window.scrollY;
    if (ref.current) {
      setIsSticky(scrolly >= top);
    }
  };

  useEffect(() => {
    if (!initialLoad.current) {
      // only bind listeners once
      return;
    }
    const cachedRef = ref.current;
    const bodyRect = document.body.getBoundingClientRect(),
      elemRect = cachedRef.getBoundingClientRect(),
      offset = triggerFromBottom
        ? elemRect.bottom - bodyRect.top
        : elemRect.top - bodyRect.top;

    setTempHeight(cachedRef.offsetHeight); // height to add when element is sticky
    setInitialWidth(cachedRef.offsetWidth); // width of element
    window.addEventListener("scroll", (e) => {
      handleScroll(offset);
    });

    window.addEventListener("resize", (e) => {
      handleScroll(offset);
      setTempHeight(cachedRef.offsetHeight); // height to add when element is sticky
      setInitialWidth(
        ref.current ? ref.current.offsetWidth : cachedRef.offsetWidth,
      ); // TODO: this only works if the element is not sticky
    });

    initialLoad.current = false;
  }, [ref]);

  // store previous sticky state
  const prevStickyState = useRef(isSticky);
  const currentlyAnimating = useRef(false);

  useEffect(() => {
    if (currentlyAnimating.current) return;

    const baseStyle = {
      transition: "transform 0.3s ease-out, opacity 0.3s ease-out",
      willChange: "transform, opacity",
      position: "fixed",
      width: `${initialWidth}px`,
    };

    const animateIn = () => {
      Object.assign(ref.current.style, {
        ...baseStyle,
        opacity: 0,
        transition: "",
        transform: "translateY(-100%)",
      });

      requestAnimationFrame(() => {
        setTimeout(() => {
          Object.assign(ref.current.style, {
            transition: baseStyle.transition,
            opacity: 1,
            transform: "translateY(0%)",
          });
        }, 300); // A short delay to ensure the browser registers the initial style change
      });

      tempRef.current.style.display = "block";
      currentlyAnimating.current = false;
    };

    const animateOut = () => {
      ref.current.style.position = "relative";
      ref.current.style.width = "auto";
      tempRef.current.style.display = "none";

      currentlyAnimating.current = false;
    };

    if (isSticky && !prevStickyState.current) {
      currentlyAnimating.current = true;
      animateIn();
    } else if (!isSticky && prevStickyState.current) {
      currentlyAnimating.current = true;
      animateOut();
    }

    prevStickyState.current = isSticky;
  }, [isSticky, initialWidth]);

  // Function to clone children with additional props
  const childrenWithProps = React.Children.map(children, (child) => {
    // Checking if child is a valid React element before cloning
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { isSticky });
    }
    return child;
  });

  return (
    <>
      <div
        className={`${top} ${className} ${
          isSticky ? stickyClass : notStickyClass
        }`}
        ref={ref}
      >
        {childrenWithProps}
      </div>
      <div
        ref={tempRef}
        style={{ height: tempHeight, width: 1, display: "none" }}
      ></div>
    </>
  );
}
